import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, ShieldCheck, Loader2, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VerificationPanel() {
  const { user, refresh } = useAuth();
  const [emailStage, setEmailStage] = useState("idle"); // idle | code
  const [phoneStage, setPhoneStage] = useState("idle");
  const [emailCode, setEmailCode] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [phone, setPhone] = useState("");
  const [demoCode, setDemoCode] = useState("");
  const [busy, setBusy] = useState("");

  const call = async (fn, key) => { setBusy(key); try { return await fn(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); } };

  const startEmail = () => call(async () => { await api.post("/verify/email/start"); setEmailStage("code"); toast.success("Verification code emailed to you"); }, "es");
  const confirmEmail = () => call(async () => { await api.post("/verify/email/confirm", { code: emailCode }); toast.success("Email verified!"); refresh(); setEmailStage("idle"); }, "ec");
  const startPhone = () => call(async () => { const { data } = await api.post("/verify/phone/start", { phone }); setPhoneStage("code"); setDemoCode(data.demo_code || ""); toast.info("SMS demo mode — code shown below"); }, "ps");
  const confirmPhone = () => call(async () => { await api.post("/verify/phone/confirm", { code: phoneCode }); toast.success("Phone verified!"); refresh(); setPhoneStage("idle"); }, "pc");

  const verified = user.email_verified || user.phone_verified;

  return (
    <div className="mt-6" data-testid="verification-panel">
      <p className="kicker mb-4">Voter Verification {verified && <span className="ml-2 text-emerald-accent">✓ Verified</span>}</p>
      <div className="card-lux p-6">
        <p className="mb-5 flex items-center gap-2 text-sm text-amber-100/70"><ShieldCheck className="h-4 w-4 text-gold" /> Verified voters unlock <span className="text-gold">+10 bonus votes per day</span> and a verified badge on every vote.</p>
        <div className="grid gap-6 sm:grid-cols-2">
          {/* Email */}
          <div>
            <p className="mb-2 flex items-center gap-2 text-sm text-amber-50"><Mail className="h-4 w-4 text-gold" /> Email {user.email_verified && <Check className="h-4 w-4 text-emerald-accent" />}</p>
            {user.email_verified ? <p className="text-xs text-emerald-accent">Verified</p> : emailStage === "idle" ? (
              <button onClick={startEmail} disabled={busy === "es"} className="btn-ghost-gold text-xs py-2 inline-flex items-center gap-2" data-testid="verify-email-start">{busy === "es" && <Loader2 className="h-3 w-3 animate-spin" />} Send code to {user.email}</button>
            ) : (
              <div className="flex gap-2">
                <input value={emailCode} onChange={(e) => setEmailCode(e.target.value)} placeholder="6-digit code" data-testid="verify-email-code" className="w-28 rounded-lg border border-gold/25 bg-obsidian px-3 py-2 text-sm text-amber-50 outline-none focus:border-gold" />
                <button onClick={confirmEmail} disabled={busy === "ec"} className="btn-gold text-xs py-2" data-testid="verify-email-confirm">Confirm</button>
              </div>
            )}
          </div>
          {/* Phone */}
          <div>
            <p className="mb-2 flex items-center gap-2 text-sm text-amber-50"><Phone className="h-4 w-4 text-gold" /> Phone (SMS) {user.phone_verified && <Check className="h-4 w-4 text-emerald-accent" />}</p>
            {user.phone_verified ? <p className="text-xs text-emerald-accent">Verified</p> : phoneStage === "idle" ? (
              <div className="flex gap-2">
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+254…" data-testid="verify-phone-input" className="w-32 rounded-lg border border-gold/25 bg-obsidian px-3 py-2 text-sm text-amber-50 outline-none focus:border-gold" />
                <button onClick={startPhone} disabled={busy === "ps" || !phone} className="btn-ghost-gold text-xs py-2" data-testid="verify-phone-start">Send code</button>
              </div>
            ) : (
              <div>
                {demoCode && <p className="mb-2 rounded bg-gold/10 px-2 py-1 text-xs text-gold">Demo code: <span className="font-mono">{demoCode}</span></p>}
                <div className="flex gap-2">
                  <input value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)} placeholder="6-digit code" data-testid="verify-phone-code" className="w-28 rounded-lg border border-gold/25 bg-obsidian px-3 py-2 text-sm text-amber-50 outline-none focus:border-gold" />
                  <button onClick={confirmPhone} disabled={busy === "pc"} className="btn-gold text-xs py-2" data-testid="verify-phone-confirm">Confirm</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
